import { useEffect, useState } from "react";
import { MdOutlineTimer } from "react-icons/md";
import "./BuySell.css";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Timer from "./Timer";
import QuickAmountButtons from "./QuickAmountButtons";
import CustomButton from "./CustomButton";
import { getGoldApi } from "../redux/features/goldSlice";
import { useDispatch } from "react-redux";
import { useMatch } from "react-router-dom";

const BuySell = ({
  metalType,
  transactionType,
  goldApiData,
  portfolioApiData,
  callQuantityAPI,
  isAmount = true,
  userAmt,
}) => {
  const {
    gBuy = 0,
    gSell = 0,
    sBuy = 0,
    sSell = 0,
  } = goldApiData?.data?.[0]?.[0] || {};

  const {
    augmont: {
      gold: { balQuantity: goldBalQuantity = 0 } = {},
      silver: { balQuantity: silverBalQuantity = 0 } = {},
    } = {},
  } = portfolioApiData?.[0] || {};

  const { t } = useTranslation();
  const { inRupee, inGram, inputAmountPlaceholder, inputWeightPlaceholder } =
    t("buySellPage");
  const { pureGold, goldExpire, goldInLocker } = t("buySellPage.gold");
  const { pureSilver, silverExpire, silverInLocker } = t("buySellPage.silver");

  const goldMatch = useMatch("/dashboard/gold/*");
  const silverMatch = useMatch("/dashboard/silver/*");

  const dispatch = useDispatch();
  const [activeAmount, setActiveAmount] = useState(null);
  const [timer, setTimer] = useState(180); // 3 minutes in seconds
  const [error, setError] = useState("Minimum purchase of ₹100");
  const [qtyError, setQtyError] = useState("");

  // ============================================================

  const [status, setStatus] = useState({
    isGold: true,
    isAmount: true,
  });

  useEffect(() => {
    setStatus((prev) => ({
      ...prev,
      isAmount: isAmount,
    }));
  }, [isAmount]);

  useEffect(() => {
    if (goldMatch || silverMatch) {
      setStatus((prev) => ({
        ...prev,
        isGold: !!goldMatch, // true if goldMatch is truthy
      }));
    }
  }, [goldMatch, silverMatch]);

  const [calValue, setCalValue] = useState({
    amount: userAmt || 100,
    quantity: 0,
  });

  var maxQty = "";

  useEffect(() => {
    const x = 1000000 / 1.03;
    const livePrice = status.isGold ? gBuy : sBuy;
    maxQty = Math.floor(x / livePrice);

    setQtyError(`Maximum purchase of ${maxQty}gm`);
  }, [status.isGold, status.isAmount]);

  const handleInputChange = (e) => {
    let value = e.target.value;

    // Set error messages upfront
    setError(
      status.isAmount
        ? "Minimum purchase of ₹100"
        : `Maximum quantity is: ${maxQty}gm`
    );

    // Calculate maxQty for grams
    if (!status.isAmount) {
      const x = 1000000 / 1.03;
      const livePrice = status.isGold ? gBuy : sBuy;
      maxQty = Math.floor(x / livePrice);
      if (parseFloat(value) > maxQty) {
        return;
      }
    }

    // Max purchase validation
    if (parseFloat(value) > 1000000 || parseFloat(calValue.amount) > 1000000) {
      setError("You can't buy more than 10 lakhs");
      return;
    }

    // Limit decimal places
    if (value.includes(".")) {
      const [intPart, decPart] = value.split(".");
      const maxDecimals = status.isAmount ? 2 : 3; // 2 for Rupees, 3 for Grams
      if (decPart.length > maxDecimals) return;
    }

    if (status.isAmount) {
      // User typing Rupees
      setCalValue((prev) => ({ ...prev, amount: value }));

      let livePrice;
      if (status.isGold) {
        livePrice = transactionType === "goldBuy" ? gBuy : gSell;
      } else {
        livePrice = transactionType === "silverBuy" ? sBuy : sSell;
      }

      const final = (parseFloat(value) / livePrice).toFixed(3); // quantity → 3 decimals
      setCalValue((prev) => ({ ...prev, quantity: final }));
    } else {
      // User typing Grams
      setCalValue((prev) => ({ ...prev, quantity: value }));

      let livePrice;
      if (status.isGold) {
        livePrice = transactionType === "goldBuy" ? gBuy : gSell;
      } else {
        livePrice = transactionType === "silverBuy" ? sBuy : sSell;
      }

      const final = (parseFloat(value) * livePrice).toFixed(2); // amount → 2 decimals
      setCalValue((prev) => ({ ...prev, amount: final }));
    }
  };

  const handleAmountButtonClick = (amount) => {
    setActiveAmount(amount);
    const value = amount.replace("₹", "");
    const metal_type = metalType == "GOLD" ? "GOLD" : "SILVER";
    if (status.isAmount) {
      // setRupeeValue(value);
      setCalValue((prev) => ({
        ...prev,
        amount: value,
      }));
      if (metal_type == "GOLD") {
        let livePrice = transactionType == "goldBuy" ? gBuy : gSell;
        let final = (value / livePrice).toFixed(3);
        // setGramValue(final);
        setCalValue((prev) => ({
          ...prev,
          quantity: final,
        }));
      } else {
        let livePrice = transactionType == "silverBuy" ? sBuy : sSell;
        let final = (value / livePrice).toFixed(3);
        // setGramValue(final);
        setCalValue((prev) => ({
          ...prev,
          quantity: final,
        }));
      }
    } else {
      // setGramValue(value);
      setCalValue((prev) => ({
        ...prev,
        quantity: value,
      }));
      if (metal_type == "GOLD") {
        let livePrice = transactionType == "goldBuy" ? gBuy : gSell;
        let final = (value * livePrice).toFixed(3);
        // setRupeeValue(final);
        setCalValue((prev) => ({
          ...prev,
          amount: final,
        }));
      } else {
        let livePrice = transactionType == "silverBuy" ? sBuy : sSell;
        let final = (value * livePrice).toFixed(3);
        // setRupeeValue(final);
        setCalValue((prev) => ({
          ...prev,
          amount: final,
        }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const amount = parseFloat(calValue.amount) || 0;
    const quantity = parseFloat(calValue.quantity) || 0;
    if (amount < 100) {
      toast.error(
        `Minimum ${
          transactionType === "goldBuy" || transactionType === "silverBuy"
            ? "purchase"
            : "sell"
        } amount is ₹100`
      );
      return;
    } else if (amount > 1000000) {
      toast.error("Maximum purchase amount is ₹1000000");
      return;
    }

    if (!status.isAmount) {
      const livePrice = status.isGold ? gBuy : sBuy;
      const x = 1000000 / 1.03;

      const maxQty = Math.floor(x / livePrice);
      if (quantity > maxQty) {
        toast.error(`Maximum quantity is: ${maxQty}gm`);
        return;
      }
    }
    if (transactionType === "goldSell") {
      const hiiiii = portfolioApiData?.[0]?.augmont?.gold?.balQuantity;
      const conditionCheck = Number(calValue.quantity) > Number(hiiiii);

      if (conditionCheck) {
        toast.error("you can't sell more than in locker");
        return;
      }
    }

    if (transactionType === "silverSell") {
      const hiiiii = portfolioApiData?.[0]?.augmont?.silver?.balQuantity;
      const conditionCheck = Number(calValue.quantity) > Number(hiiiii);

      if (conditionCheck) {
        toast.error("you can't sell more than in locker");
        return;
      }
    }

    callQuantityAPI(
      calValue.amount,
      calValue.quantity,
      status,
      transactionType
    );
  };

  useEffect(() => {
    let countdown;
    if (timer > 0) {
      countdown = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      clearInterval(countdown);
    }
    return () => clearInterval(countdown);
  }, [timer]);

  useEffect(() => {
    dispatch(getGoldApi());
  }, [timer == 0]);

  useEffect(() => {
    const metal_type = metalType === "GOLD" ? "GOLD" : "SILVER";
    const livePrice =
      metal_type === "GOLD"
        ? transactionType === "goldBuy"
          ? gBuy
          : gSell
        : transactionType === "silverBuy"
        ? sBuy
        : sSell;

    if (livePrice) {
      const quantity = (calValue.amount / livePrice).toFixed(3);
      setCalValue((prev) => ({
        ...prev,
        quantity: quantity,
      }));
    }
  }, [gBuy, sBuy, userAmt]);

  // const [minMaxQty, setMinMaxQty] = useState({
  //   minQty: "",
  //   maxQty: "",
  // });

  // useEffect(() => {
  //   const livePrice = metalType === "GOLD" ? gBuy : sBuy;

  //   const minQty = (100 / livePrice).toFixed(3);

  //   const x = 1000000 / 1.03;
  //   const maxQty = Math.floor(x / livePrice);

  //   setMinMaxQty({
  //     minQty,
  //     maxQty,
  //   });
  // }, [metalType, gBuy, sBuy]);

  // console.log("minMax quantaty=========>>>>>", minMaxQty);

  return (
    <>
      <div className="buySell_main_container universal_card">
        {/* LIVE PRICE========== */}
        <div className="inner-container">
          <div className="buySell_price_container">
            <div className="live-container">
              <span className="live-text">LIVE</span>
              <span className="live-dot"></span>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div className="medium-text">
                {["goldBuy", "goldSell", "silverBuy", "silverSell"].includes(
                  transactionType
                ) ? (
                  <span
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: "bold",
                      color: ["goldBuy", "goldSell"].includes(transactionType)
                        ? "#EDBF00"
                        : "#A6A6A6",
                    }}
                  >
                    ₹
                    {transactionType === "goldBuy"
                      ? gBuy
                      : transactionType === "goldSell"
                      ? gSell
                      : transactionType === "silverBuy"
                      ? sBuy
                      : sSell}
                    /gm{" "}
                    {(transactionType === "goldBuy" ||
                      transactionType === "silverBuy") && <sup>+3% GST</sup>}
                  </span>
                ) : (
                  <span>Invalid transaction type</span>
                )}
              </div>
            </div>
            <div>{metalType === "GOLD" ? pureGold : pureSilver}</div>
          </div>
          <div className="buySell_timer_container">
            <div>
              <img
                src={
                  metalType === "GOLD"
                    ? "/images/gold_shield.png"
                    : "/images/silver_shield.png"
                }
                alt="shield"
                width="20%"
              />
            </div>
            <div
              className="timer"
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "5px",
              }}
            >
              <MdOutlineTimer />
              <div>{metalType == "GOLD" ? goldExpire : silverExpire}</div>
              <Timer />
            </div>
          </div>
        </div>
        {/* AMOUNT & GRAM btns===========  */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div className="test_btn_box">
            <button
              className={`test_btn_provider ${
                status.isAmount ? "test_btn_active" : "test_btn_inactive"
              }`}
              onClick={() =>
                setStatus((prevStatus) => ({
                  ...prevStatus,
                  isAmount: true,
                }))
              }
            >
              {inRupee}
            </button>
            <button
              className={`test_btn_provider ${
                status.isAmount ? "test_btn_inactive" : "test_btn_active"
              }`}
              onClick={() =>
                setStatus((prevStatus) => ({
                  ...prevStatus,
                  isAmount: false,
                }))
              }
            >
              {inGram}
            </button>
          </div>
        </div>
        {/* INPUT FIELD========== */}
        <form onSubmit={handleSubmit}>
          <div className="input-container">
            <div
              className={status.isGold ? "input-border" : "input_border_silver"}
            >
              <div className="input-1">
                <span
                  className="rupee"
                  style={{
                    color: metalType == "SILVER" ? "#A6A6A6" : "#FFDF00",
                    margin: "0px 10px",
                  }}
                >
                  {status.isAmount ? "₹" : "gm"}
                </span>
              </div>

              <input
                type="text"
                inputMode="decimal"
                placeholder={
                  status.isAmount
                    ? inputAmountPlaceholder
                    : inputWeightPlaceholder
                }
                value={status.isAmount ? calValue.amount : calValue.quantity}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "") {
                    handleInputChange(e);
                    return;
                  }
                  if (
                    /^\d+\.?\d*$/.test(value) && // Starts with digit, optional decimal
                    (value.match(/\./g) || []).length <= 1 // Max one decimal point
                  ) {
                    // Special case: if starts with "0" followed by digits (not .), prevent "0123"
                    if (/^0[0-9]/.test(value) && !value.includes(".")) {
                      e.target.value = value.slice(0, -1); // Remove last character
                      return;
                    }

                    handleInputChange(e);
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedData = e.clipboardData.getData("text/plain");

                  // Same validation as onChange
                  if (
                    /^\d+\.?\d*$/.test(pastedData) &&
                    (pastedData.match(/\./g) || []).length <= 1
                  ) {
                    const newEvent = {
                      target: {
                        value: pastedData,
                        name: e.target.name,
                      },
                    };
                    handleInputChange(newEvent);
                  }
                }}
                className="input-field"
                style={{
                  border: "none",
                  outline: "none",
                  fontSize: "20px",
                  width: "100%",
                }}
                required
              />

              <div className="input-2">
                <span
                  className="rupee"
                  style={{
                    color: metalType == "SILVER" ? "#A6A6A6" : "#FFDF00",
                    margin: "0px 10px",
                  }}
                >
                  {status.isAmount
                    ? `${calValue.quantity}gm`
                    : `₹${calValue.amount}`}
                </span>
              </div>
            </div>
          </div>
          <div className="error_container">
            {/* ERROR=======  */}
            <div
              style={{
                visibility:
                  transactionType === "goldBuy" ||
                  transactionType === "silverBuy"
                    ? "visible"
                    : "hidden",
              }}
            >
              {status.isAmount
                ? error && (
                    <div className="error-message" style={{ color: "grey" }}>
                      {error}
                    </div>
                  )
                : qtyError && (
                    <div className="error-message" style={{ color: "grey" }}>
                      {qtyError}
                    </div>
                  )}
            </div>

            {/* IN LOCKER********* */}
            <div>
              {metalType == "GOLD" ? goldInLocker : silverInLocker}{" "}
              <span
                style={{
                  color: metalType == "GOLD" ? "##FFDF00" : "#A6A6A6",
                  fontWeight: "bold",
                }}
              >
                {metalType === "GOLD" ? goldBalQuantity : silverBalQuantity}
                gm
              </span>
            </div>
          </div>
          <QuickAmountButtons
            isRupeeSelected={status.isAmount}
            activeAmount={activeAmount}
            handleAmountButtonClick={handleAmountButtonClick}
          />

          <div
            style={{ display: "flex", justifyContent: "center", gap: "2rem" }}
          >
            <CustomButton
              type="submit"
              label={
                transactionType === "goldBuy" || transactionType === "silverBuy"
                  ? "Proceed To Buy"
                  : "Proceed To Sell"
              }
            />
          </div>
        </form>
      </div>
    </>
  );
};

export default BuySell;
